import React from 'react';
import PropTypes from 'prop-types';
import GameDisplayMetadataSection from '../shared/GameDisplayMetadataSection';
import ActivityImageUploadSection from '../shared/ActivityImageUploadSection';
import {buildImageHandlersFromProps} from '../shared/buildImageHandlersFromProps';
import BackgammonGuidelineModal from './BackgammonGuidelineModal';
import {
  BACKGAMMON_BACKGROUND_IMAGE_FIELDS,
  BACKGAMMON_BACKGROUND_SECTION_CONFIG
} from './backgammonImageFieldConfig';

const BackgammonGameDisplayForm = ({
  languages,
  activeTabIndex,
  setActiveTabIndex,
  currentLangData,
  updateLangField,
  gameData,
  handleGameImageUpload,
  handleGameImageDelete,
  handleLogoImageUpload,
  handleLogoImageDelete,
  handleBackgroundMenuUpload,
  handleBackgroundMenuDelete,
  handleBackgroundImageUpload,
  handleBackgroundImageDelete,
  handleBackgroundVSUpload,
  handleBackgroundVSDelete,
  handleBackgroundWaitingUpload,
  handleBackgroundWaitingDelete,
  handleBackgroundExitUpload,
  handleBackgroundExitDelete,
  gameImageProgress,
  logoImageProgress,
  backgroundMenuProgress,
  backgroundImageProgress,
  backgroundVSProgress,
  backgroundWaitingProgress,
  backgroundExitProgress,
  gameImageValidationError = '',
  logoImageValidationError = '',
  backgroundMenuValidationError = '',
  backgroundImageValidationError = '',
  backgroundVSValidationError = '',
  backgroundWaitingValidationError = '',
  backgroundExitValidationError = '',
  handleGameImageFileError,
  handleLogoImageFileError,
  handleBackgroundMenuFileError,
  handleBackgroundImageFileError,
  handleBackgroundVSFileError,
  handleBackgroundWaitingFileError,
  handleBackgroundExitFileError,
  showGuidelines,
  setShowGuidelines
}) => {
  const backgroundImageHandlers = buildImageHandlersFromProps(
    {
      handleBackgroundMenuUpload,
      handleBackgroundMenuDelete,
      handleBackgroundMenuFileError,
      backgroundMenuProgress,
      backgroundMenuValidationError,
      handleBackgroundImageUpload,
      handleBackgroundImageDelete,
      handleBackgroundImageFileError,
      backgroundImageProgress,
      backgroundImageValidationError,
      handleBackgroundVSUpload,
      handleBackgroundVSDelete,
      handleBackgroundVSFileError,
      backgroundVSProgress,
      backgroundVSValidationError,
      handleBackgroundWaitingUpload,
      handleBackgroundWaitingDelete,
      handleBackgroundWaitingFileError,
      backgroundWaitingProgress,
      backgroundWaitingValidationError,
      handleBackgroundExitUpload,
      handleBackgroundExitDelete,
      handleBackgroundExitFileError,
      backgroundExitProgress,
      backgroundExitValidationError
    },
    BACKGAMMON_BACKGROUND_IMAGE_FIELDS
  );

  return (
    <div
      className='mb-8 flex flex-col gap-6'
      data-testid='backgammon-display-form'>
      <GameDisplayMetadataSection
        languages={languages}
        activeTabIndex={activeTabIndex}
        setActiveTabIndex={setActiveTabIndex}
        currentLangData={currentLangData}
        updateLangField={updateLangField}
        gameData={gameData}
        handleGameImageUpload={handleGameImageUpload}
        handleGameImageDelete={handleGameImageDelete}
        handleLogoImageUpload={handleLogoImageUpload}
        handleLogoImageDelete={handleLogoImageDelete}
        gameImageProgress={gameImageProgress}
        logoImageProgress={logoImageProgress}
        gameImageValidationError={gameImageValidationError}
        logoImageValidationError={logoImageValidationError}
        handleGameImageFileError={handleGameImageFileError}
        handleLogoImageFileError={handleLogoImageFileError}
        setShowGuidelines={setShowGuidelines}
      />

      <ActivityImageUploadSection
        {...BACKGAMMON_BACKGROUND_SECTION_CONFIG}
        gameData={gameData}
        imageFields={BACKGAMMON_BACKGROUND_IMAGE_FIELDS}
        imageHandlers={backgroundImageHandlers}
        setShowGuidelines={setShowGuidelines}
      />

      <BackgammonGuidelineModal
        showGuidelines={showGuidelines}
        setShowGuidelines={setShowGuidelines}
      />
    </div>
  );
};

BackgammonGameDisplayForm.propTypes = {
  languages: PropTypes.array.isRequired,
  activeTabIndex: PropTypes.number.isRequired,
  setActiveTabIndex: PropTypes.func.isRequired,
  currentLangData: PropTypes.object.isRequired,
  updateLangField: PropTypes.func.isRequired,
  gameData: PropTypes.object.isRequired,
  handleGameImageUpload: PropTypes.func.isRequired,
  handleGameImageDelete: PropTypes.func.isRequired,
  handleLogoImageUpload: PropTypes.func.isRequired,
  handleLogoImageDelete: PropTypes.func.isRequired,
  handleBackgroundMenuUpload: PropTypes.func.isRequired,
  handleBackgroundMenuDelete: PropTypes.func.isRequired,
  handleBackgroundImageUpload: PropTypes.func.isRequired,
  handleBackgroundImageDelete: PropTypes.func.isRequired,
  handleBackgroundVSUpload: PropTypes.func.isRequired,
  handleBackgroundVSDelete: PropTypes.func.isRequired,
  handleBackgroundWaitingUpload: PropTypes.func.isRequired,
  handleBackgroundWaitingDelete: PropTypes.func.isRequired,
  handleBackgroundExitUpload: PropTypes.func.isRequired,
  handleBackgroundExitDelete: PropTypes.func.isRequired,
  gameImageProgress: PropTypes.number,
  logoImageProgress: PropTypes.number,
  backgroundMenuProgress: PropTypes.number,
  backgroundImageProgress: PropTypes.number,
  backgroundVSProgress: PropTypes.number,
  backgroundWaitingProgress: PropTypes.number,
  backgroundExitProgress: PropTypes.number,
  gameImageValidationError: PropTypes.string,
  logoImageValidationError: PropTypes.string,
  backgroundMenuValidationError: PropTypes.string,
  backgroundImageValidationError: PropTypes.string,
  backgroundVSValidationError: PropTypes.string,
  backgroundWaitingValidationError: PropTypes.string,
  backgroundExitValidationError: PropTypes.string,
  handleGameImageFileError: PropTypes.func,
  handleLogoImageFileError: PropTypes.func,
  handleBackgroundMenuFileError: PropTypes.func,
  handleBackgroundImageFileError: PropTypes.func,
  handleBackgroundVSFileError: PropTypes.func,
  handleBackgroundWaitingFileError: PropTypes.func,
  handleBackgroundExitFileError: PropTypes.func,
  showGuidelines: PropTypes.bool.isRequired,
  setShowGuidelines: PropTypes.func.isRequired
};

export default React.memo(BackgammonGameDisplayForm);
